import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { toast } from 'src/components/ui/toast';
import { Action, Subject } from '@erp/authz';
import { useCan } from 'src/hooks/useCan';
import { useUnsavedChangesGuard } from 'src/hooks/useUnsavedChangesGuard';
import {
  useArchiveTaskType,
  useTaskType,
  useTaskTypes,
  useUpdateTaskType,
} from 'src/api/rpc/hooks/tasks/useTaskTypes';
import {
  buildTaskTypeBehavior,
  type TaskTypeEditorState,
} from '../components/cards/task-types/taskTypeAdmin';
import { stageOptionsFromTaskTypes } from '../components/cards/task-types/taskTypeStageTree';
import {
  canSaveTaskTypeEditor,
  isTaskTypeEditorDirty,
  mutationErrorMessage,
  resolveTaskTypeEditor,
} from './taskTypeDetailEditor';
import { useMetricFieldOptions } from '../components/cards/task-types/useMetricFieldOptions';

export function useTaskTypeDetailEditor(taskTypeId: string) {
  const gt = useGT();
  const navigate = useNavigate();
  const [canManage] = useCan(Action.Update, Subject.TaskType);
  const [canArchive] = useCan(Action.Delete, Subject.TaskType);

  const { data: taskType, isLoading, isError } = useTaskType(taskTypeId);
  const { data: taskTypes } = useTaskTypes();
  const updateTaskType = useUpdateTaskType();
  const archiveTaskType = useArchiveTaskType();

  const [draft, setDraft] = useState<TaskTypeEditorState | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const editor = resolveTaskTypeEditor(draft, taskType);
  const dirty = isTaskTypeEditorDirty(editor);
  const unsavedGuard = useUnsavedChangesGuard(dirty);
  const metricFields = useMetricFieldOptions(editor);
  const canSave = canSaveTaskTypeEditor({ canManage, editor, metricFields });

  function returnToList() {
    void navigate({
      to: '/settings/organization/$section',
      params: { section: 'task-types' },
    });
  }

  async function handleSave() {
    if (!editor?.editing || !canSave) return;
    try {
      await updateTaskType.mutateAsync({
        id: editor.editing.id,
        name: editor.name.trim(),
        description: editor.description.trim() || null,
        ...buildTaskTypeBehavior(editor),
      });
      toast.success(
        gt('Task type saved', { context: 'toast after saving a task type' }),
      );
      // Stay on the page. Dropping the draft makes the editor read the
      // refetched type, so the form shows what was saved and is clean again.
      setDraft(null);
    } catch (error) {
      toast.error(
        mutationErrorMessage(
          error,
          gt('Failed to save the task type', {
            context: 'toast after a task type save failed',
          }),
        ),
      );
    }
  }

  async function handleArchive() {
    if (!taskType || dirty) return;
    try {
      await archiveTaskType.mutateAsync({ id: taskType.id });
      setArchiveOpen(false);
      toast.success(
        gt('Task type archived', {
          context: 'toast after archiving a task type',
        }),
      );
      returnToList();
    } catch (error) {
      toast.error(
        mutationErrorMessage(
          error,
          gt('Failed to archive the task type', {
            context: 'toast after a task type archive failed',
          }),
        ),
      );
    }
  }

  const stageOptions = stageOptionsFromTaskTypes(taskTypes ?? []);

  return {
    gt,
    canManage,
    canArchive,
    taskType,
    isLoading,
    isError,
    editor,
    canSave,
    isDirty: dirty,
    archiveOpen,
    setArchiveOpen,
    setDraft,
    stageOptions,
    unsavedGuard,
    returnToList,
    handleBack: () => unsavedGuard.guard(() => returnToList()),
    handleSave,
    handleArchive,
    isSaving: updateTaskType.isPending,
    isArchiving: archiveTaskType.isPending,
  };
}
